import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mantine/hooks";
import {
  Modal,
  Text,
  Flex,
  Paper,
  Button,
  Divider,
  Tooltip,
  Checkbox,
  Anchor,
  Box,
  Grid,
  ThemeIcon,
  ActionIcon,
  Alert,
  List,
  Center,
  Loader,
  Stack,
} from "@mantine/core";
import {
  IconX,
  IconInfoCircle,
  IconCoins,
  IconCircleCheck,
} from "@tabler/icons-react";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import CollaborationUnlockIllustration from "./CollaborationUnlockIllustration";

const GTSETU_MUTUAL_NDA_URL = "https://gtsetu.com/non-disclosure-agreement/";
const NAVY = "#04305c";

const UNLOCK_BENEFITS = [
  "View complete company profile and contact details",
  "Access and share documents securely",
  "Chat and communicate within the workspace",
  "Collaborate with confidence in a verified environment",
] as const;

export type PaymentConfirmPayload = { method: "credits"; acceptNda?: boolean };

interface PaymentMethodModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: (payload: PaymentConfirmPayload) => void | Promise<void>;
  loading?: boolean;
  creditsRequired?: number;
  showNote?: boolean;
  insufficientCreditsTone?: "collaboration" | "generic";
  intent?: "collaboration" | "verification";
}

function ModalHeader({
  title,
  subtitle,
  onClose,
  isMobile,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  isMobile: boolean;
}) {
  return (
    <Flex justify="space-between" align="flex-start" gap="sm" mb={{ base: "md", sm: "lg" }}>
      <Box miw={0} style={{ flex: 1 }}>
        <Text fw={700} fz={{ base: "lg", sm: "xl" }} c={NAVY} lh={1.25}>
          {title}
        </Text>
        {subtitle ? (
          <Text fz={{ base: "xs", sm: "sm" }} c="#64748B" mt={6} lh={1.45}>
            {subtitle}
          </Text>
        ) : null}
      </Box>
      <ActionIcon
        variant="subtle"
        color="gray"
        onClick={onClose}
        aria-label="Close"
        size={isMobile ? "md" : "lg"}
        style={{ flexShrink: 0 }}
      >
        <IconX size={20} stroke={1.75} />
      </ActionIcon>
    </Flex>
  );
}

function CreditSummaryRows({
  availableCredits,
  creditsRequired,
  balanceLoading,
  labelSuffix = "",
  compact,
}: {
  availableCredits: number;
  creditsRequired: number;
  balanceLoading?: boolean;
  labelSuffix?: string;
  compact?: boolean;
}) {
  const remainingBalance = availableCredits - creditsRequired;
  const display = (n: number) => (balanceLoading ? "—" : String(n));
  const rowPy = compact ? 10 : 12;
  const rowPx = compact ? "sm" : "md";

  return (
    <Paper radius="md" style={{ border: "1px solid #E2E8F0", overflow: "hidden" }}>
      <Flex justify="space-between" align="center" gap="xs" px={rowPx} py={rowPy} wrap="nowrap">
        <Text fz={{ base: "xs", sm: "sm" }} c="#475569" style={{ flex: 1, minWidth: 0 }}>
          Available Credits{labelSuffix}
        </Text>
        <Text fz={{ base: "xs", sm: "sm" }} fw={500} c="#0F172A" style={{ flexShrink: 0 }}>
          {display(availableCredits)}
        </Text>
      </Flex>
      <Divider color="#E2E8F0" />
      <Flex justify="space-between" align="center" gap="xs" px={rowPx} py={rowPy} wrap="nowrap">
        <Text fz={{ base: "xs", sm: "sm" }} c="#475569" style={{ flex: 1, minWidth: 0 }}>
          Credits Required{labelSuffix}
        </Text>
        <Text fz={{ base: "xs", sm: "sm" }} fw={500} c="#0F172A" style={{ flexShrink: 0 }}>
          {creditsRequired}
        </Text>
      </Flex>
      <Divider color="#E2E8F0" />
      <Flex justify="space-between" align="center" gap="xs" px={rowPx} py={rowPy} wrap="nowrap">
        <Text fz={{ base: "xs", sm: "sm" }} fw={700} c="#0F172A" style={{ flex: 1, minWidth: 0 }}>
          Remaining Balance{labelSuffix}
        </Text>
        <Text fz={{ base: "xs", sm: "sm" }} fw={700} c={remainingBalance >= 0 ? "#15803D" : "#DC2626"} style={{ flexShrink: 0 }}>
          {balanceLoading ? "—" : remainingBalance}
        </Text>
      </Flex>
    </Paper>
  );
}

const checkboxLabelStyles = { body: { alignItems: "flex-start" as const }, label: { lineHeight: 1.45 } };

function PaymentMethodModal({
  opened,
  onClose,
  onConfirm,
  loading = false,
  creditsRequired = 14,
  showNote = true,
  insufficientCreditsTone = "collaboration",
  intent = "collaboration",
}: PaymentMethodModalProps) {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 576px)", false, { getInitialValueInEffect: true });
  const isCompactLayout = useMediaQuery("(max-width: 768px)", false, { getInitialValueInEffect: true });
  const [availableCredits, setAvailableCredits] = useState(0);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [ndaAccepted, setNdaAccepted] = useState(false);

  const isCollaboration = intent === "collaboration";
  const hasEnoughCredits = availableCredits >= creditsRequired;
  const showInsufficientOnly = isCollaboration && !balanceLoading && !hasEnoughCredits;
  const showUnlock = isCollaboration && hasEnoughCredits && !balanceLoading;
  const showVerificationConfirm = !isCollaboration && !balanceLoading;

  useEffect(() => {
    if (!opened) return;
    setNdaAccepted(false);
  }, [opened]);

  useEffect(() => {
    if (!opened) return;

    let cancelled = false;
    setBalanceLoading(true);

    const fetchWalletBalance = async () => {
      try {
        const walletData = await trpc.wallet.getBalance.query();
        if (cancelled) return;
        setAvailableCredits(walletData.available || 0);
      } catch (error) {
        if (cancelled) return;
        handleTrpcError(error);
        setAvailableCredits(0);
      } finally {
        if (!cancelled) setBalanceLoading(false);
      }
    };

    fetchWalletBalance();
    return () => {
      cancelled = true;
    };
  }, [opened, creditsRequired]);

  const payDisabled = useMemo(() => {
    if (balanceLoading || !hasEnoughCredits) return true;
    if (isCollaboration) return !ndaAccepted;
    return false;
  }, [balanceLoading, hasEnoughCredits, isCollaboration, ndaAccepted]);

  const payDisabledReason = useMemo(() => {
    if (!hasEnoughCredits) {
      return insufficientCreditsTone === "collaboration"
        ? "Insufficient credits — please add credits to your wallet"
        : "Insufficient credits";
    }
    if (!ndaAccepted) return "Please accept the NDA";
    return undefined;
  }, [hasEnoughCredits, ndaAccepted, insufficientCreditsTone]);

  const refundNote = showNote ? (
    <Text fz="xs" c="#94A3B8" mt="md" lh={1.5}>
      <Text span fw={700} fz="xs" c="#94A3B8">
        Note:
      </Text>{" "}
      If the collaboration is unsuccessful, your credits will be automatically refunded to your wallet for future use.
    </Text>
  ) : null;

  const handlePay = () => {
    if (payDisabled) return;
    onConfirm({
      method: "credits",
      acceptNda: isCollaboration ? ndaAccepted : false,
    });
  };

  const modalMaxWidth = showInsufficientOnly ? 400 : showUnlock ? 920 : 480;

  const handleBuyCredits = () => {
    onClose();
    navigate("/credits/buy");
  };

  const modalBody = balanceLoading ? (
    <Center py="xl">
      <Loader color="primary" size="lg" />
    </Center>
  ) : showInsufficientOnly ? (
    <>
      <Flex justify="space-between" align="center" gap="sm" mb="lg">
        <Text fw={700} fz={{ base: "lg", sm: "xl" }} c="#BE123C">
          Insufficient credits
        </Text>
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={onClose}
          aria-label="Close"
          size={isMobile ? "md" : "lg"}
          style={{ flexShrink: 0 }}
        >
          <IconX size={20} stroke={1.75} />
        </ActionIcon>
      </Flex>
      <Button
        fullWidth
        radius="md"
        size={isMobile ? "md" : "sm"}
        variant="light"
        color="red"
        onClick={handleBuyCredits}
      >
        Buy credits
      </Button>
    </>
  ) : showUnlock ? (
    <>
      <ModalHeader
        title="Unlock Full Access"
        subtitle="Use collaboration credits to activate this match"
        onClose={onClose}
        isMobile={!!isMobile}
      />

      <Grid gutter={{ base: "lg", md: "xl" }} align="stretch">
        <Grid.Col span={{ base: 12, md: 7 }} order={{ base: 2, md: 1 }}>
          <Alert
            icon={<IconInfoCircle size={18} />}
            color="blue"
            variant="light"
            radius="md"
            mb="md"
            styles={{
              root: { backgroundColor: "#EFF6FF", border: "1px solid #BFDBFE" },
              message: { color: "#1E3A5F" },
            }}
          >
            <Text fz="sm" component="span" fw={600}>
              You&apos;re one step away!
            </Text>{" "}
            Use collaboration credits to unlock full company details and start collaborating.
          </Alert>

          <Text fw={700} fz="sm" mb="xs" c="#0F172A">
            Credit Summary
          </Text>
          <CreditSummaryRows
            availableCredits={availableCredits}
            creditsRequired={creditsRequired}
            balanceLoading={balanceLoading}
            compact={!!isCompactLayout}
          />

          <Paper
            radius="md"
            p="md"
            mt="md"
            style={{ backgroundColor: "#FFFBEB", border: "1px solid #FDE68A" }}
          >
            <Flex gap="sm" align="flex-start">
              <ThemeIcon size={36} radius="md" variant="light" color="yellow" style={{ flexShrink: 0 }}>
                <IconCoins size={20} />
              </ThemeIcon>
              <Box>
                <Text fw={700} fz="sm" c="#0F172A" mb={4}>
                  How collaboration credits work
                </Text>
                <Text fz="sm" c="#475569" lh={1.5}>
                  Credits are used when a mutual match is created. They help us ensure serious intent and maintain a
                  trusted collaboration environment.
                </Text>
              </Box>
            </Flex>
          </Paper>

          <Checkbox
            mt="lg"
            checked={ndaAccepted}
            onChange={(e) => setNdaAccepted(e.currentTarget.checked)}
            styles={checkboxLabelStyles}
            label={
              <Text fz={{ base: "xs", sm: "sm" }} c="#334155" lh={1.45}>
                I have read and agree to the terms of the{" "}
                <Anchor
                  href={GTSETU_MUTUAL_NDA_URL}
                  target="_blank"
                  rel="noreferrer"
                  fw={600}
                  onClick={(e) => e.stopPropagation()}
                >
                  Mutual Non-Disclosure Agreement (NDA)
                </Anchor>
                .
              </Text>
            }
          />

          <Stack gap="sm" mt="xl">
            <Tooltip label={payDisabledReason} disabled={!payDisabled} withArrow>
              <Button
                fullWidth
                radius="md"
                size={isMobile ? "md" : "sm"}
                style={{ backgroundColor: !payDisabled ? NAVY : undefined }}
                disabled={payDisabled}
                loading={loading}
                onClick={handlePay}
              >
                {isMobile ? `Use ${creditsRequired} Credits` : `Use ${creditsRequired} Credits & Continue`}
              </Button>
            </Tooltip>
            <Button
              fullWidth
              variant="default"
              onClick={onClose}
              radius="md"
              size={isMobile ? "md" : "sm"}
              disabled={loading}
              style={{ borderColor: "#CBD5E1", color: "#0F172A" }}
            >
              Cancel
            </Button>
          </Stack>

          {refundNote}
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 5 }} order={{ base: 1, md: 2 }}>
          <Flex
            direction="column"
            align="center"
            justify="center"
            h="100%"
            gap={{ base: "md", md: "lg" }}
            mb={{ base: "sm", md: 0 }}
          >
            <CollaborationUnlockIllustration compact={!!isCompactLayout} />
            <Box w="100%">
              <Text fw={700} fz={{ base: "xs", sm: "sm" }} c="#0F172A" mb="sm">
                With full access, you can:
              </Text>
              <List
                spacing="xs"
                center={false}
                icon={
                  <ThemeIcon color="green" size={20} radius="xl" variant="light">
                    <IconCircleCheck size={12} stroke={2.5} />
                  </ThemeIcon>
                }
              >
                {UNLOCK_BENEFITS.map((benefit) => (
                  <List.Item key={benefit}>
                    <Text fz={{ base: "xs", sm: "sm" }} c="#334155" lh={1.45}>
                      {benefit}
                    </Text>
                  </List.Item>
                ))}
              </List>
            </Box>
          </Flex>
        </Grid.Col>
      </Grid>
    </>
  ) : showVerificationConfirm ? (
    <>
      <ModalHeader title="Confirm Credit Usage" onClose={onClose} isMobile={!!isMobile} />

      <Text c="#64748B" fz={{ base: "xs", sm: "sm" }} mb="lg" lh={1.5}>
        You are about to use {creditsRequired} credit(s) to run registry verification. If the registry confirms your
        company, this charge applies immediately (admin-assisted paths may charge on approval).
      </Text>

      <CreditSummaryRows
        availableCredits={availableCredits}
        creditsRequired={creditsRequired}
        balanceLoading={balanceLoading}
        labelSuffix=":"
        compact={!!isCompactLayout}
      />

      {refundNote}

      <Tooltip
        label={!hasEnoughCredits ? "Insufficient credits" : undefined}
        disabled={hasEnoughCredits}
        withArrow
      >
        <Button
          fullWidth
          mt="xl"
          radius="md"
          size={isMobile ? "md" : "sm"}
          onClick={handlePay}
          disabled={!hasEnoughCredits}
          loading={loading}
          styles={{
            root: {
              backgroundColor: !hasEnoughCredits ? "#E2E8F0" : NAVY,
              color: !hasEnoughCredits ? "#94A3B8" : "#fff",
              fontWeight: 600,
              "&:hover": !hasEnoughCredits ? undefined : { backgroundColor: "#032847" },
            },
          }}
        >
          Confirm & Proceed
        </Button>
      </Tooltip>
    </>
  ) : null;

  const useFullscreen = isMobile && !showInsufficientOnly && !balanceLoading && showUnlock;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      radius={useFullscreen ? 0 : "lg"}
      size={
        showInsufficientOnly
          ? "sm"
          : useFullscreen
            ? "100%"
            : isCompactLayout && showUnlock
              ? "lg"
              : showUnlock
                ? "xl"
                : "md"
      }
      fullScreen={useFullscreen}
      withCloseButton={false}
      padding={showInsufficientOnly ? "lg" : useFullscreen ? "md" : isCompactLayout ? "lg" : "xl"}
      overlayProps={{ backgroundOpacity: 0.45, blur: 2 }}
      styles={{
        inner: { overflow: "hidden" },
        content: {
          maxWidth: useFullscreen ? "100%" : modalMaxWidth,
          width: "100%",
          overflow: "hidden",
        },
        body: {
          paddingTop: 8,
          overflow: "hidden",
        },
      }}
    >
      {modalBody}
    </Modal>
  );
}

export default PaymentMethodModal;
